import { useEffect, useId, useState } from 'react'
import { Link } from 'react-router'
import { useQueryClient } from '@tanstack/react-query'
import { type NodeEgress, type NodeInfo } from '../../api'
import { NODES_KEY } from '../Nodes'
import { FactorCancelled } from '../SecondFactor'
import { STALE_MS, relayKeys, requestAsOf, routePath, type NodeInfoAsOf, type RelayRole, type RelayRoute } from './api'
import { ServerImpact, WordField, actionError, useConfirmedAction } from './confirm'
import { isDanger, useDefaultGroup, useDryRun } from './groupAdmin'
import { WarningNotes } from './Warnings'
import { dataAge, type RelayLive } from './live'
import { useNow } from '../../hooks'
import { plural } from './format'
import { nodeLegs } from './nodes'
import { RELAY_STATE_SPEC } from './states'
import { NetPanel, ROLE_HINT, ROLE_LABEL, RoleGlyph, StateBadge } from './ui'
import { Note } from '../../ui'
import { t } from '../../i18n'
import { groupPath } from './groups'
import { tn } from './nodeSwitch'

/*
 * Roles, egress and relay of a node (04-admin-api.md §3.1, §5.1; handoff §3):
 * PATCH /api/admin/nodes/{id} {roles, egress, relay_enabled}. Only what the API
 * takes in R0: the ingress, egress and hub roles (relay = transit is R2: kept as
 * it is), the egress mode and the node's relay switch. The egress mode is
 * changed here only (Node settings points here), so both confirmations of
 * decision 48 go through one flow.
 *
 * Confirmation, by the owner's matrix:
 * - every change: the inline confirmation in this panel (no modal), with what it
 *   touches as far as the console knows (legs, routes, streams) and what
 *   platforms see;
 * - the center decides whether the change breaks the air: switching off egress /
 *   hub or the relay, or egress via_center → direct, of a node with live legs on
 *   the routes touched. Then it refuses the first request (400 confirm_required
 *   with `impact`, nothing applied) and the panel shows the center's impact and
 *   the word DISABLE; the button stays off until it matches. The second factor is
 *   the server's too: a 403 totp_required opens the code window and the same
 *   request goes again with the code (confirm.tsx).
 * The state changes only from the answer (and the stream after it): until then
 * the panel says APPLYING.
 */

const EDITABLE: RelayRole[] = ['ingress', 'egress', 'hub']
const EGRESS: { value: NodeEgress; label: string; hint: string }[] = [
  { value: 'direct', label: 'Direct', hint: 'Streams with OBS on this node go to platforms from this node' },
  { value: 'via_center', label: 'Via center', hint: 'Streams with OBS on this node go to platforms from a hub, over the relay' },
]

export function RolesRelay({ n, live, routes }: { n: NodeInfo; live: RelayLive; routes: RelayRoute[] }) {
  const qc = useQueryClient()
  const act = useConfirmedAction()
  const now = useNow(1000)
  const titleId = useId()
  const cur = n.roles ?? []
  const relayOn = !!n.relay?.enabled
  const [roles, setRoles] = useState<RelayRole[]>(cur)
  const [egress, setEgress] = useState<NodeEgress>(n.egress)
  const [relay, setRelay] = useState(relayOn)
  const [confirm, setConfirm] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [done, setDone] = useState('')
  // the server's answer (or a stream event) changed the node: the form starts from it
  const key = `${cur.join(',')}|${relayOn}|${n.egress}`
  useEffect(() => {
    if (busy) return
    setRoles(n.roles ?? [])
    setEgress(n.egress)
    setRelay(!!n.relay?.enabled)
    setConfirm(false)
    act.reset()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  const removed = cur.filter((r) => !roles.includes(r))
  const added = roles.filter((r) => !cur.includes(r))
  const relayChange = relay !== relayOn
  const egressChange = egress !== n.egress
  const changed = removed.length > 0 || added.length > 0 || relayChange || egressChange
  const egressRoutes = routes.filter((r) => r.path[r.path.length - 1]?.id === n.id)
  const ingressRoutes = routes.filter((r) => r.path[0]?.id === n.id)
  const toDirect = egressChange && n.egress === 'via_center' && egress === 'direct'
  // what breaks the air, as far as the console knows (the center decides: confirm.tsx)
  const disabling = removed.includes('egress') || removed.includes('hub') || (relayOn && !relay) || toDirect
  const { legs, legsUp } = nodeLegs(n)
  const liveLegs = legs > 0 || routes.length > 0
  const touched = toDirect && !removed.length && !(relayOn && !relay) ? ingressRoutes : routes
  const age = dataAge(live, now)
  const unreachable = live.failing && age !== null && age > STALE_MS
  const why = n.status === 'revoked' ? 'The node is revoked.' : unreachable ? 'The center is not answering: changes are off until it does.' : ''
  const empty = roles.length === 0
  const asked = act.asked
  // GEG-1a: a node group member's roles follow its membership roles (the center answers 409
  // member_roles_managed here); egress and the relay switch stay editable. DS: GEU-01.
  const managed = n.failover?.mode === 'group' && !!n.failover.member && !!n.failover.group

  const reset = () => {
    setRoles(cur)
    setEgress(n.egress)
    setRelay(relayOn)
    setConfirm(false)
    act.reset()
    setErr('')
  }
  const edit = () => {
    setDone('')
    setConfirm(false)
    act.reset()
  }

  const bodyOf = () => {
    const body: { roles?: RelayRole[]; relay_enabled?: boolean; egress?: NodeEgress } = {}
    if (removed.length || added.length) body.roles = [...cur.filter((r) => !EDITABLE.includes(r)), ...EDITABLE.filter((r) => roles.includes(r))]
    if (egressChange) body.egress = egress
    if (relayChange) body.relay_enabled = relay
    return body
  }
  // GROUP-UI-3 (NG-18 C): the center's warnings for this change, first in the confirmation
  // (V2: the last node of the automatic address; a hub of a group with offload); they never block
  const dg = useDefaultGroup()
  const dry = useDryRun(confirm && changed && !empty ? { method: 'PATCH', path: `/api/admin/nodes/${encodeURIComponent(n.id)}`, body: bodyOf() } : null, dg.supported)
  const warnDanger = isDanger(dry.warnings)

  const apply = async () => {
    const body = bodyOf()
    setBusy(true)
    setErr('')
    setDone('')
    try {
      const r = await act.run((c) =>
        requestAsOf<NodeInfoAsOf>(`/api/admin/nodes/${encodeURIComponent(n.id)}`, { method: 'PATCH', body: { ...body, ...(c.confirm ? { confirm: c.confirm } : {}), ...(c.totp ? { totp: c.totp } : {}) } }),
      )
      if (!r.ok) return // the center showed what it touches and asks for the word
      qc.setQueryData(relayKeys.node(n.id), r.data)
      void qc.invalidateQueries({ queryKey: NODES_KEY })
      setConfirm(false)
      setDone(
        `Saved. ${n.status === 'online' ? `${n.name} gets it over its channel now` : `${n.name} gets it when it connects`}; the peers list moves to a new version and the other nodes acknowledge it.`,
      )
    } catch (e) {
      if (e instanceof FactorCancelled) return
      setErr(actionError(e, `PATCH /api/admin/nodes/${n.id}`))
    } finally {
      setBusy(false)
    }
  }

  const toggle = (r: RelayRole) => {
    edit()
    setRoles((x) => (x.includes(r) ? x.filter((y) => y !== r) : [...x, r]))
  }

  const spec = n.relay ? (RELAY_STATE_SPEC[n.relay.state] ?? RELAY_STATE_SPEC.unregistered) : undefined
  const locked = !!why || busy

  return (
    <NetPanel
      title="Roles and relay"
      testId="node-roles"
      aside={
        busy ? (
          <StateBadge spec={{ glyph: '◔', word: 'APPLYING', color: 'start', border: 'dashed', hint: 'Waiting for the center to answer' }} testId="node-roles-applying" />
        ) : (
          spec && <StateBadge spec={spec} testId="node-roles-relay-state" />
        )
      }
    >
      <div className="flex flex-col gap-4">
        <fieldset className="flex flex-col gap-2" disabled={locked}>
          <legend className="mb-2 text-xs text-muted">Roles (at least one). Relay = transit hop comes later and is kept as it is.</legend>
          {managed && (
            <Note data-testid="node-roles-managed">
              {tn('geg.roles.managed', { group: n.failover!.group_name ?? n.failover!.group! })}{' '}
              <Link to={groupPath(n.failover!.group!)} className="text-accent hover:underline">{t('geg.roles.openGroup', { group: n.failover!.group_name ?? n.failover!.group! })}</Link>
            </Note>
          )}
          <div className="flex flex-wrap gap-2">
            {EDITABLE.map((r) => {
              const on = roles.includes(r)
              return (
                <button
                  key={r}
                  type="button"
                  role="checkbox"
                  aria-checked={on}
                  data-testid={`role-${r}`}
                  title={ROLE_HINT[r]}
                  disabled={managed}
                  onClick={() => toggle(r)}
                  className={`inline-flex min-h-9 items-center gap-2 rounded-md border px-3 text-sm disabled:opacity-50 max-md:min-h-[44px] ${on ? 'border-live/50 bg-live/[.12] text-live' : 'border-line-2 border-dashed text-muted hover:text-text-soft'}`}
                >
                  <span aria-hidden className="font-mono text-xs">
                    {on ? '☑' : '☐'}
                  </span>
                  <RoleGlyph role={r} />
                  {ROLE_LABEL[r]}
                </button>
              )
            })}
            {cur.includes('relay') && (
              <span className="inline-flex min-h-9 items-center gap-2 rounded-md border border-line px-3 text-sm text-muted" title={ROLE_HINT.relay}>
                <RoleGlyph role="relay" /> Relay (kept)
              </span>
            )}
          </div>
          {empty && <p className="text-xs text-fail">A node needs at least one role.</p>}
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-2" role="radiogroup" aria-label="Egress of the streams with OBS on this node" data-testid="node-egress">
            <span className="text-sm text-text-soft">Egress</span>
            {EGRESS.map((o) => {
              const on = egress === o.value
              return (
                <button
                  key={o.value}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  data-testid={`egress-${o.value}`}
                  title={o.hint}
                  onClick={() => (edit(), setEgress(o.value))}
                  className={`inline-flex min-h-9 items-center gap-2 rounded-md border px-3 text-sm disabled:opacity-50 max-md:min-h-[44px] ${on ? 'border-live/50 bg-live/[.12] text-live' : 'border-line-2 border-dashed text-muted hover:text-text-soft'}`}
                >
                  <span aria-hidden className="font-mono text-xs">
                    {on ? '◉' : '○'}
                  </span>
                  {o.label}
                </button>
              )
            })}
            <span className="text-xs text-muted">({n.egress === 'via_center' ? 'via center now' : 'direct now'})</span>
          </div>
          <label className="mt-2 inline-flex min-h-9 items-center gap-2 text-sm max-md:min-h-[44px]">
            <input type="checkbox" data-testid="relay-enabled" checked={relay} onChange={(e) => (edit(), setRelay(e.target.checked))} className="h-4 w-4 accent-[var(--live)]" />
            Relay on this node
            <span className="text-xs text-muted">({relayOn ? 'on now' : 'off now'})</span>
          </label>
          {live.settings && !live.settings.enabled && (
            <p className="text-xs text-warn" data-testid="relay-center-off">
              ▲ Relay is switched off for the whole center (RELAY_ENABLED): the node's switch takes effect once it is on.
            </p>
          )}
        </fieldset>

        <p className="text-xs text-muted" data-testid="node-roles-now">
          Now: {legs ? `${legsUp} of ${plural(legs, 'relay leg')} up` : 'no relay legs'} · {plural(routes.length, 'route')} through this node
          {n.relay?.no_path?.length ? ` · ${plural(n.relay.no_path.length, 'stream')} without a path` : ''}.
        </p>

        {why && (
          <p className="text-xs text-muted" data-testid="node-roles-off">
            {why}
          </p>
        )}

        {confirm && changed && !empty ? (
          <div
            role="group"
            aria-labelledby={titleId}
            data-testid="node-roles-confirm"
            data-word={asked?.word ?? ''}
            className={`flex flex-col gap-3 rounded-md border p-3 ${disabling || asked ? 'border-fail/50 bg-fail/[.06]' : 'border-warn/50 bg-warn/[.06]'}`}
          >
            <WarningNotes warnings={dry.warnings} ctx={{ name: n.name, group: n.failover?.group_name }} testId="node-roles-warnings" />
            <p id={titleId} className={`font-medium ${disabling || asked ? 'text-fail' : 'text-warn'}`}>
              Apply to {n.name}?
            </p>
            <ul className="list-disc space-y-1 pl-5 text-[13px] text-text-soft" data-testid="node-roles-changes">
              {added.map((r) => (
                <li key={`+${r}`}>Role {ROLE_LABEL[r].toLowerCase()} on: {ADD_TEXT[r]}</li>
              ))}
              {removed.map((r) => (
                <li key={`-${r}`}>Role {ROLE_LABEL[r].toLowerCase()} off: {REMOVE_TEXT[r]}</li>
              ))}
              {egressChange && <li data-testid="node-roles-egress-change">{egress === 'direct' ? EGRESS_TEXT.direct : EGRESS_TEXT.via_center}</li>}
              {relayChange && <li>{relay ? 'Relay on: the node may carry relay legs again once it is in the peers list.' : 'Relay off: its live legs close now (SHUTDOWN), no new legs start.'}</li>}
            </ul>
            {asked ? (
              <ServerImpact impact={asked.impact} node={n.name} testId="node-roles-server-impact" />
            ) : (
              <div className="text-[13px]" data-testid="node-roles-impact">
                <p className="font-medium text-text">What it touches</p>
                <p className="text-text-soft">
                  {legs ? `${legsUp} of ${plural(legs, 'relay leg')} up` : 'No relay legs'} · {plural(routes.length, 'route')} through {n.name}
                  {egressRoutes.length > 0 && ` (${egressRoutes.length} leave to platforms here)`}
                  {ingressRoutes.length > 0 && ` (${ingressRoutes.length} with OBS here)`}.
                </p>
                {touched.length > 0 && (
                  <p className="mt-1 flex flex-wrap gap-x-2 gap-y-1 font-mono text-xs">
                    {touched.slice(0, 8).map((r) => (
                      <Link key={r.route_id} to={routePath(r.route_id)} className="text-text-soft underline underline-offset-2">
                        {r.stream}
                      </Link>
                    ))}
                    {touched.length > 8 && <span className="text-muted">+{touched.length - 8} more</span>}
                  </p>
                )}
                {disabling && liveLegs && (
                  <p className="mt-1 text-xs text-muted" data-testid="node-roles-will-ask">
                    The center checks the live legs when you apply: if the change breaks the air, it shows what it takes and asks to type DISABLE, then your code. Nothing changes before that.
                  </p>
                )}
              </div>
            )}
            <div className="text-[13px]">
              <p className="font-medium text-text">What platforms see</p>
              <p className="text-text-soft" data-testid="node-roles-platforms">
                {platformsText({ disabling: disabling && (liveLegs || !!asked), toDirect, toVia: egressChange && egress === 'via_center', streamsHere: ingressRoutes.length + (n.streams ?? 0) > 0 })}
              </p>
            </div>
            {asked && <WordField word={asked.word} value={act.word} onChange={act.setWord} testId="node-roles-word" />}
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => (setConfirm(false), act.reset())} className="inline-flex min-h-9 items-center rounded-md px-3 text-sm text-muted hover:text-text max-md:min-h-[44px]">
                Back
              </button>
              <button
                type="button"
                data-testid="node-roles-apply"
                disabled={busy || !!why || (!!asked && !act.matches) || dry.pending}
                onClick={() => void apply()}
                className={`inline-flex min-h-9 items-center rounded-md border px-3 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50 max-md:min-h-[44px] ${disabling || asked || warnDanger ? 'border-fail/50 text-fail hover:bg-fail/[.12]' : 'border-live bg-live/[.12] text-live hover:bg-live/20'}`}
              >
                {busy ? 'Applying…' : asked ? `Confirm and apply to ${n.name}` : disabling ? `Apply to ${n.name}` : 'Apply'}
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              data-testid="node-roles-review"
              disabled={!changed || empty || locked}
              onClick={() => setConfirm(true)}
              className="inline-flex min-h-9 items-center rounded-md border border-live bg-live/[.12] px-3 text-sm font-medium text-live hover:bg-live/20 disabled:cursor-not-allowed disabled:opacity-50 max-md:min-h-[44px]"
            >
              Review change…
            </button>
            {changed && (
              <button type="button" onClick={reset} className="inline-flex min-h-9 items-center px-2 text-sm text-muted hover:text-text max-md:min-h-[44px]">
                Undo
              </button>
            )}
          </div>
        )}
        {err && (
          <p role="alert" className="rounded-md border border-fail/50 bg-fail/[.07] px-3 py-2 text-sm text-fail" data-testid="node-roles-error">
            {err}
          </p>
        )}
        {done && (
          <p role="status" className="text-sm text-text-soft" data-testid="node-roles-done">
            {done}
          </p>
        )}
      </div>
    </NetPanel>
  )
}

function platformsText(o: { disabling: boolean; toDirect: boolean; toVia: boolean; streamsHere: boolean }): string {
  if (o.disabling && o.toDirect)
    return 'Streams with OBS on this node stop going through a hub: their relay routes end and this node publishes to the platforms itself — each platform sees one reconnect.'
  if (o.disabling)
    return 'Streams through this node lose their relay path: the controller moves each to another ready hub (platforms reconnect when the egress changes) or marks it NO PATH; outputs then hold the platform session for the stream’s grace time and stop.'
  if (o.toVia && o.streamsHere) return 'Streams with OBS on this node get a relay route to a hub and go to the platforms from there — each platform sees one reconnect.'
  return 'Nothing: running streams keep their paths; the change applies to new routes.'
}

const EGRESS_TEXT: Record<NodeEgress, string> = {
  direct: 'Egress direct: this node publishes the streams of its encoders to the platforms itself and gets their keys.',
  via_center: 'Egress via center: the streams of its encoders go to a hub over the relay and leave to the platforms there; this node gets no platform keys.',
}
const ADD_TEXT: Record<RelayRole, string> = {
  ingress: 'encoders may be placed on this node.',
  egress: 'the node may hold platform sessions of relayed streams.',
  hub: 'via-center streams may use this node as their hub.',
  relay: 'transit hop (later stage).',
  integrations: 'not a media role.',
}
const REMOVE_TEXT: Record<RelayRole, string> = {
  ingress: 'new encoders are not placed here; running ones stay connected.',
  egress: 'streams that leave to platforms here must move to another egress.',
  hub: 'via-center streams that use this node as hub must move to another hub.',
  relay: 'no transit (later stage).',
  integrations: 'not a media role.',
}
